import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface PipelineStage {
  id: string
  name: string
  deal_count: number
  deal_value: number
  avg_deal_size: number
  conversion_rate: number
  avg_days_in_stage: number
}

interface Deal {
  id: string
  company: string
  value: number
  stage: string
  probability: number
  owner: string
  close_date: string
  days_open: number
  next_action: string
}

interface PipelineForecast {
  id: string
  period: string
  pipeline_value: number
  weighted_value: number
  expected_close: number
  best_case: number
  deal_count: number
}

interface Statistics {
  total_pipeline_value: number
  weighted_pipeline: number
  avg_deal_size: number
  avg_sales_cycle: number
  win_rate: number
  pipeline_coverage: number
}

export default function SalesPipelinePage() {
  const [stages, setStages] = useState<PipelineStage[]>([])
  const [deals, setDeals] = useState<Deal[]>([])
  const [forecasts, setForecasts] = useState<PipelineForecast[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [stagesRes, dealsRes, forecastsRes, statsRes] = await Promise.all([
        fetch('/api/salespipeline/stages'),
        fetch('/api/salespipeline/deals'),
        fetch('/api/salespipeline/forecast'),
        fetch('/api/salespipeline/statistics')
      ])
      if (stagesRes.ok) setStages(await stagesRes.json())
      if (dealsRes.ok) setDeals(await dealsRes.json())
      if (forecastsRes.ok) setForecasts(await forecastsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching sales pipeline data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getStageBadge = (stage: string) => {
    if (stage === 'Qualified') return 'bg-blue-100 text-blue-800'
    if (stage === 'Demo') return 'bg-purple-100 text-purple-800'
    if (stage === 'Proposal') return 'bg-yellow-100 text-yellow-800'
    if (stage === 'Negotiation') return 'bg-orange-100 text-orange-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Sales Pipeline</h1>
          <p className="mt-2 text-gray-600">Phase 233 - Pipeline stages, deals, and forecasts</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Pipeline Value</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.total_pipeline_value)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Weighted</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.weighted_pipeline)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Deal Size</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.avg_deal_size)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Sales Cycle</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_sales_cycle} days</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Win Rate</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.win_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Coverage</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.pipeline_coverage}x</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Pipeline Stages</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stages.filter(s => !['Lead', 'Closed Won', 'Closed Lost'].includes(s.name)).map((stage) => (
              <div key={stage.id} className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 mb-1">{stage.name}</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(stage.deal_value)}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-500">Deals</p>
                    <p className="font-medium">{stage.deal_count}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Avg Days</p>
                    <p className="font-medium">{stage.avg_days_in_stage}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Conversion</p>
                    <p className="font-medium text-green-600">{stage.conversion_rate}%</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Avg Size</p>
                    <p className="font-medium text-purple-600">{formatCurrency(stage.avg_deal_size)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Active Deals</h2>
            </div>
            <div className="p-6 space-y-4">
              {deals.map((deal) => (
                <div key={deal.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{deal.company}</span>
                    <span className="text-lg font-bold text-blue-600">{formatCurrency(deal.value)}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStageBadge(deal.stage)}`}>{deal.stage}</span>
                    <span className="text-gray-500 text-sm">{deal.probability}% probability</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>Owner: {deal.owner} | Close: {deal.close_date}</p>
                    <p className="text-purple-600">{deal.next_action}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Pipeline Forecast</h2>
            </div>
            <div className="p-6 space-y-4">
              {forecasts.map((forecast) => (
                <div key={forecast.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{forecast.period}</span>
                    <span className="text-lg font-bold text-green-600">{formatCurrency(forecast.expected_close)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Pipeline</p>
                      <p className="font-medium">{formatCurrency(forecast.pipeline_value)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Weighted</p>
                      <p className="font-medium text-blue-600">{formatCurrency(forecast.weighted_value)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Best Case</p>
                      <p className="font-medium text-purple-600">{formatCurrency(forecast.best_case)}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">{forecast.deal_count} deals</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
